// Zeichenfläche für Unterschriften — ausgelagert aus der öffentlichen
// Signier-Seite (pages/public/SignierenSeite.tsx), damit auch das Digitale
// Übergabeprotokoll dieselbe Komponente nutzen kann. Verhalten unverändert
// übernommen (Strichstärke, Farbe, Pointer-Capture, Canvas-Maße etc.).
//
// Zwei Hüllen um dieselbe Zeichenfläche: „dunkel“ (Standard, öffentliche Signier-Seite wie bisher)
// und „sheet“ (Übergabe-Redesign, design_handoff_uebergabe §4.6 / Screen 14: zentriertes Sheet
// 640 px mit gestrichelter Linie „Hier unterschreiben“ — die Linie liegt nur darüber, nicht im PNG).
import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Check, Eraser, Signature } from '@phosphor-icons/react';
import { Sheet } from './Sheet';

interface UnterschriftPadProps {
  onUebernehmen: (dataUrl: string) => void;
  onSchliessen: () => void;
  istMobil: boolean;
  titel?: string;
  huelle?: 'dunkel' | 'sheet';
}

// ── Zeichen-Overlay für Signaturfelder ───────────────────────────────────────
export default function UnterschriftPad({
  onUebernehmen,
  onSchliessen,
  istMobil,
  titel = 'Unterschrift',
  huelle = 'dunkel',
}: UnterschriftPadProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const zeichnendRef = useRef(false);
  const letztePosRef = useRef<{ x: number; y: number } | null>(null);
  const [hatInhalt, setHatInhalt] = useState(false);
  // Canvas-Breite an schmale Bildschirme anpassen (Höhe proportional ~0.37×
  // wie am Desktop, damit die Signatur nicht verzerrt wirkt). Bei Drehung des
  // Geräts (z. B. Handy quer) wird nachgezogen — sonst bliebe die im Hochformat
  // gemessene, schmalere Breite stehen und der im Querformat gewonnene Platz
  // für den Unterschriften-Strich ungenutzt.
  const [breite, setBreite] = useState(() => Math.min(600, window.innerWidth - 32));
  useEffect(() => {
    const onResize = () => {
      // Ein Größenwechsel setzt Canvas-Attribute neu — der Browser leert die
      // Zeichenfläche dabei ohnehin, also den Inhalt-Status mitziehen, damit
      // „Übernehmen" nicht auf einem leeren Bild aktiv bleibt.
      setBreite(Math.min(600, window.innerWidth - 32));
      setHatInhalt(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const hoehe = Math.round(breite * 0.37);

  const holePos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    // Wird die Fläche per CSS schmaler dargestellt als ihre Pixelbreite (schmale Hülle),
    // Zeigerposition auf Canvas-Pixel umrechnen — sonst läge der Strich neben dem Finger.
    const sx = rect.width ? canvas.width / rect.width : 1;
    const sy = rect.height ? canvas.height / rect.height : 1;
    return { x: (e.clientX - rect.left) * sx, y: (e.clientY - rect.top) * sy };
  };

  const pointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try { canvas.setPointerCapture(e.pointerId); } catch { /* Browser ohne Pointer-Capture-Support */ }
    zeichnendRef.current = true;
    letztePosRef.current = holePos(e);
  };

  const pointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!zeichnendRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const pos = holePos(e);
    const letzte = letztePosRef.current || pos;
    ctx.strokeStyle = '#1a1d24';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(letzte.x, letzte.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    letztePosRef.current = pos;
    setHatInhalt(true);
  };

  const pointerEnde = () => {
    zeichnendRef.current = false;
    letztePosRef.current = null;
  };

  const loeschen = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHatInhalt(false);
  };

  const uebernehmen = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hatInhalt) return;
    onUebernehmen(canvas.toDataURL('image/png'));
  };

  const flaeche = (
    <canvas
      ref={canvasRef}
      width={breite}
      height={hoehe}
      className="block w-full touch-none cursor-crosshair"
      style={{ aspectRatio: `${breite} / ${hoehe}`, touchAction: 'none' }}
      onPointerDown={pointerDown}
      onPointerMove={pointerMove}
      onPointerUp={pointerEnde}
      onPointerCancel={pointerEnde}
    />
  );

  if (huelle === 'sheet') {
    return (
      <Sheet titel={titel} icon={Signature} iconFarbe="#5856d6" onSchliessen={onSchliessen} breite={istMobil ? 10000 : 640}>
        {/* Breite nach verfügbarer Höhe begrenzen (Handy quer): Kopf + Knöpfe + Ränder ≈ 210 px, Seitenverhältnis ≈ 2,7. */}
        <div className="relative w-full overflow-hidden" style={{ maxWidth: `min(100%, calc((100dvh - 210px) * ${(breite / hoehe).toFixed(3)}))`, margin: '0 auto',
          borderRadius: 20, background: '#fff', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
          {flaeche}
          <div aria-hidden className="pointer-events-none absolute" style={{ left: 24, right: 24, bottom: 42, borderBottom: '1.5px dashed rgba(15,27,45,0.25)' }} />
          <span aria-hidden className="pointer-events-none absolute" style={{ left: 24, bottom: 16, fontSize: 12, fontWeight: 700, color: 'rgba(15,27,45,0.4)' }}>Hier unterschreiben</span>
        </div>
        <div className="flex" style={{ gap: 8 }}>
          <button type="button" onClick={loeschen} className="inline-flex items-center" data-tip="Löschen – Zeichenfläche leeren"
            style={{ height: 48, padding: '0 18px', gap: 6, borderRadius: 15, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink)' }}>
            <Eraser size={15} weight="bold" />Löschen
          </button>
          <button type="button" onClick={uebernehmen} disabled={!hatInhalt} className="inline-flex flex-1 items-center justify-center"
            data-tip={hatInhalt ? 'Übernehmen – Unterschrift ins Protokoll übernehmen' : 'Übernehmen – Bitte zuerst unterschreiben'}
            style={{ height: 48, gap: 8, borderRadius: 15, border: 'none', cursor: hatInhalt ? 'pointer' : 'not-allowed', opacity: hatInhalt ? 1 : 0.45,
              fontFamily: 'inherit', fontSize: 14, fontWeight: 800, background: 'var(--acc-fill)', boxShadow: 'var(--acc-sh)', color: 'var(--acc-ink)' }}>
            <Check size={15} weight="bold" />Übernehmen
          </button>
        </div>
      </Sheet>
    );
  }

  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center bg-black/70 backdrop-blur-sm ${
        istMobil ? 'items-end p-0' : 'items-center p-6'
      }`}
      onClick={onSchliessen}
    >
      <div
        className={`w-full bg-[#1a1d24] border border-white/10 p-6 sm:p-8 ${
          istMobil
            ? 'max-w-full rounded-t-[2rem] pb-[calc(env(safe-area-inset-bottom)+1.5rem)]'
            : 'max-w-xl rounded-[2rem]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-sm font-black text-white uppercase tracking-widest">{titel}</h2>
          <button
            type="button"
            onClick={onSchliessen}
            aria-label="Schließen"
            className="w-8 h-8 flex items-center justify-center rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-all"
          >
            <X size={16} />
          </button>
        </div>
        <div className="bg-white rounded-xl overflow-hidden shadow-inner mx-auto" style={{ width: breite, maxWidth: '100%' }}>
          {flaeche}
        </div>
        <div className="flex items-center justify-end gap-3 mt-5">
          <button
            type="button"
            onClick={loeschen}
            className="min-h-[44px] px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest text-white/60 border border-white/15 hover:text-white hover:border-white/30 transition-all"
          >
            Löschen
          </button>
          <button
            type="button"
            onClick={uebernehmen}
            disabled={!hatInhalt}
            className="min-h-[44px] px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest text-white bg-[#c4a77d] hover:bg-[#b3966a] transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Übernehmen
          </button>
        </div>
      </div>
    </div>
  );
}
